import API from "../../services/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  fetchAttendance,
  fetchMonthlyAttendanceCalendar,
  normalizeAttendanceStatus,
} from "../attendance/attendanceApi";
import { downloadBlob } from "../Employees/employeeFileUtils";
import { exportRowsToXlsx, toIsoDate } from "./employeeReportsUtils";

export { exportRowsToXlsx, toIsoDate };

const REGISTER_LEGEND = "P = Present · A = Absent · L = Leave · H = Holiday";

const STATUS_LABELS = {
  P: "Present",
  A: "Absent",
  HD: "Half Day",
  L: "Leave",
  PL: "Paid Leave",
  UL: "Unpaid Leave",
  WO: "Week Off",
  H: "Holiday",
  WFH: "WFH",
  OD: "On Duty",
};

export function formatAttendanceTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function formatLateBy(minutes) {
  const m = Number(minutes || 0);
  if (!m) return "—";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

export function statusDisplayLabel(status) {
  const code = normalizeAttendanceStatus(status);
  return STATUS_LABELS[code] || code || "—";
}

export function currentYearMonth() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function monthYearToKey(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export function buildEmployeeDeptMap(employees) {
  const map = {};
  for (const emp of employees || []) {
    const id = emp?.employee_id;
    if (!id) continue;
    const deptId = emp?.department_id ?? emp?.department?.department_id ?? "";
    map[String(id)] = deptId ? String(deptId) : "";
  }
  return map;
}

function filterRowsByDepartment(rows, departmentId, employeeDeptMap, employeeIdKey = "employee_id") {
  if (!departmentId) return rows;
  return (rows || []).filter((r) => employeeDeptMap[String(r[employeeIdKey])] === String(departmentId));
}

export async function fetchAbsentReport({ fromDate, toDate }) {
  const res = await API.get("/wf/reports/attendance/absent", {
    params: { from_date: fromDate, to_date: toDate },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchLateReport({ fromDate, toDate }) {
  const res = await API.get("/wf/reports/attendance/late", {
    params: { from_date: fromDate, to_date: toDate },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function buildDailyAttendanceRows({ date, departmentId, employeeId }) {
  const rows = await fetchAttendance({
    from_date: date,
    to_date: date,
    department_id: departmentId || undefined,
    employee_id: employeeId || undefined,
  });
  return (Array.isArray(rows) ? rows : []).map((r) => ({
    Employee: r.employee_name || "—",
    Status: statusDisplayLabel(r.status),
    "In Time": formatAttendanceTime(r.time_in || r.check_in_time),
    "Out Time": formatAttendanceTime(r.time_out || r.check_out_time),
  }));
}

function inScope(scopeIds, employeeId) {
  return !scopeIds || scopeIds.has(String(employeeId));
}

export async function buildMonthlySummaryRows({ year, month, departmentId, scopeIds = null }) {
  const data = await fetchMonthlyAttendanceCalendar({
    year,
    month,
    department_id: departmentId || undefined,
  });
  return (data?.employees || []).filter((emp) => inScope(scopeIds, emp.employee_id)).map((emp) => {
    const days = emp.days || {};
    let present = 0;
    let absent = 0;
    let leave = 0;
    let holiday = 0;
    Object.values(days).forEach((code) => {
      if (code === "P") present += 1;
      else if (code === "A") absent += 1;
      else if (code === "L" || code === "PL" || code === "UL") leave += 1;
      else if (code === "H") holiday += 1;
    });
    return {
      Employee: emp.employee_name || "—",
      Present: present,
      Absent: absent,
      Leave: leave,
      Holiday: holiday,
    };
  });
}

export async function buildAbsentReportRows({ fromDate, toDate, departmentId, employeeDeptMap, scopeIds = null }) {
  const raw = await fetchAbsentReport({ fromDate, toDate });
  const filtered = filterRowsByDepartment(raw, departmentId, employeeDeptMap).filter((r) =>
    inScope(scopeIds, r.employee_id)
  );
  return filtered.map((r) => ({
    Employee: r.employee_name || "—",
    "Absent Date": r.work_date || "—",
  }));
}

export async function buildLateReportRows({ fromDate, toDate, departmentId, employeeDeptMap, scopeIds = null }) {
  const [lateRows, attRows] = await Promise.all([
    fetchLateReport({ fromDate, toDate }),
    fetchAttendance({ from_date: fromDate, to_date: toDate, department_id: departmentId || undefined }),
  ]);
  const filtered = filterRowsByDepartment(lateRows, departmentId, employeeDeptMap).filter((r) =>
    inScope(scopeIds, r.employee_id)
  );
  const timeByKey = {};
  for (const r of Array.isArray(attRows) ? attRows : []) {
    timeByKey[`${r.employee_id}-${r.work_date}`] = r.time_in || r.check_in_time;
  }
  return filtered.map((r) => ({
    Employee: r.employee_name || "—",
    Date: r.work_date || "—",
    "Actual In Time": formatAttendanceTime(timeByKey[`${r.employee_id}-${r.work_date}`]),
    "Late By": formatLateBy(r.late_minutes),
  }));
}

export function registerPreviewColumns(year, month, previewDays = 5) {
  const cols = ["Employee"];
  const total = Math.min(previewDays, daysInMonth(year, month));
  for (let d = 1; d <= total; d += 1) {
    cols.push(`Day ${d}`);
  }
  return cols;
}

export function registerAllColumns(year, month) {
  const cols = ["Employee"];
  const total = daysInMonth(year, month);
  for (let d = 1; d <= total; d += 1) {
    cols.push(`Day ${d}`);
  }
  return cols;
}

export function buildRegisterRows(calendarData, year, month, previewDays = null) {
  const total = previewDays ? Math.min(previewDays, daysInMonth(year, month)) : daysInMonth(year, month);
  return (calendarData?.employees || []).map((emp) => {
    const days = emp.days || {};
    const row = { Employee: emp.employee_name || "—" };
    for (let d = 1; d <= total; d += 1) {
      row[`Day ${d}`] = days[String(d)] || "—";
    }
    return row;
  });
}

export async function buildAttendanceRegisterRows({ year, month, departmentId, previewDays = null }) {
  const data = await fetchMonthlyAttendanceCalendar({
    year,
    month,
    department_id: departmentId || undefined,
  });
  return buildRegisterRows(data, year, month, previewDays);
}

/** jsPDF's standard fonts cannot draw the rupee sign or em dash; use plain text in PDFs. */
function pdfSafeText(value) {
  return String(value).replace(/₹\s?/g, "Rs. ").replace(/—/g, "-");
}

export function exportRowsToPdf(rows, title, filename, { legend = false } = {}) {
  const columns = rows.length ? Object.keys(rows[0]) : ["Message"];
  const body = rows.length
    ? rows.map((row) => columns.map((col) => pdfSafeText(row[col] ?? "—")))
    : [["No records found"]];

  const isWide = columns.length > 6 || columns.some((col) => /^Day \d+$/i.test(col));
  const doc = new jsPDF({
    orientation: isWide ? "landscape" : "portrait",
    unit: "mm",
    format: "a4",
  });

  // Title, subtitle and table share one left margin so everything lines up.
  const MARGIN = 14;
  const generated = new Date().toLocaleString();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfSafeText(title), MARGIN, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text(`Generated ${generated}  |  Rows: ${rows.length}`, MARGIN, 22);

  let startY = 27;
  if (legend) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(REGISTER_LEGEND, MARGIN, 26);
    startY = 30;
  }
  doc.setTextColor(0, 0, 0);

  autoTable(doc, {
    head: [columns],
    body,
    startY,
    theme: "grid",
    styles: {
      fontSize: isWide ? 6.5 : 9,
      cellPadding: isWide ? 1.4 : { top: 2.4, right: 3, bottom: 2.4, left: 3 },
      textColor: [15, 23, 42],
      lineColor: [203, 213, 225],
      lineWidth: 0.15,
      valign: "middle",
    },
    // Soft header (same palette as the on-screen grid and the print view).
    headStyles: {
      fillColor: [238, 242, 255],
      textColor: [30, 58, 138],
      fontStyle: "bold",
      fontSize: isWide ? 6.5 : 9,
      lineColor: [199, 210, 254],
      lineWidth: 0.2,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: MARGIN, right: MARGIN, top: 14, bottom: 14 },
  });

  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text(pdfSafeText(title), MARGIN, pageHeight - 7);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 7, { align: "right" });
  }

  const pdfName = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  downloadBlob(doc.output("blob"), pdfName);
}
